import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ALL_FACTIONS,
  DEFAULT_EMBLEM,
  FACTIONS,
  type Emblem as EmblemData,
  type FactionId,
} from '@sfco/shared';
import { AuthLayout, AuthSeal } from './AuthLayout.js';
import { signupRequest, AuthApiError } from './api.js';
import { useAuthStore } from '../store/auth.js';
import { EmblemDesigner } from '../ui/EmblemDesigner.js';
import { cn } from '../ui/cn.js';
import { OAuthProviders } from './OAuthProviders.js';
import { Field, Input, FormError, SubmitButton } from './LoginPage.js';

export function SignupPage() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [corpName, setCorpName] = useState('');
  const [emblem, setEmblem] = useState<EmblemData>(DEFAULT_EMBLEM);
  const [startingFaction, setStartingFaction] = useState<FactionId | null>(null);
  // Belt-and-braces: the submit handler reads from this ref so a fast
  // click-then-submit can't fire `signupRequest` with stale state. The ref
  // always holds the most recent selection, even within the same React tick.
  const startingFactionRef = useRef<FactionId | null>(null);
  const chooseFaction = (fid: FactionId | null) => {
    startingFactionRef.current = fid;
    setStartingFaction(fid);
  };
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const user = await signupRequest({
        email,
        password,
        corpName,
        emblem,
        startingFaction: startingFactionRef.current,
      });
      setUser(user);
      // P23 Batch F — fresh charter goes straight into the game.
      navigate('/play', { replace: true });
    } catch (err) {
      if (err instanceof AuthApiError) {
        const msg: Record<string, string> = {
          EMAIL_TAKEN: 'That email is already registered.',
          CORP_NAME_TAKEN: 'That corp name is already taken.',
          CORP_NAME_RESERVED: 'That corp name is reserved — choose another.',
          WEAK_PASSWORD: 'Password must be at least 10 characters.',
          INVALID_INPUT: 'Some fields did not validate. Double-check and retry.',
        };
        setError(msg[err.code] ?? err.message);
      } else {
        setError('Network error — check your connection.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout wide>
      <div className="overflow-hidden rounded-lg border border-t-[3px] border-line border-t-accent bg-panel shadow-soft">
        <div className="flex items-center gap-4 border-b border-line px-6 py-5">
          <AuthSeal />
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted">
              Stellar Freight Co. · CTA Registration
            </p>
            <h1 className="mt-1 text-[22px] font-extrabold leading-tight tracking-tight text-fg">
              Register Charter
            </h1>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-6 px-6 py-6">
          {/* ── Credentials ──────────────────────────────────────── */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Corp name">
              <Input
                type="text"
                value={corpName}
                onChange={setCorpName}
                placeholder="Apex Logistics Inc."
                autoComplete="organization"
                required
              />
              <p className="mt-1 font-mono text-[10px] text-muted">
                3–32 chars · letters, digits, spaces, hyphens
              </p>
            </Field>
            <Field label="Email address">
              <Input
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="corp@example.com"
                autoComplete="email"
                required
              />
            </Field>
            <Field label="Password">
              <Input
                type="password"
                value={password}
                onChange={setPassword}
                placeholder="Min 10 characters"
                autoComplete="new-password"
                required
              />
            </Field>
          </div>

          {/* ── Starting faction ─────────────────────────────────── */}
          <section className="space-y-2">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.1em] text-fg-2">
              Starting Affiliation
            </h2>
            <p className="font-mono text-[11px] text-muted">
              +10 rep with your chosen faction, −2 with their rival (per lore §2).
              Independent charters get no bonus and no enemies.
            </p>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              <FactionCard
                active={startingFaction === null}
                onClick={() => chooseFaction(null)}
                title="Independent"
                sub="No affiliation"
                accent="var(--c-muted)"
                rivalName={null}
              />
              {ALL_FACTIONS.map((fid) => {
                const rivalId = FACTIONS[fid].rival;
                return (
                  <FactionCard
                    key={fid}
                    active={startingFaction === fid}
                    onClick={() => chooseFaction(fid)}
                    title={FACTIONS[fid].name}
                    sub={FACTIONS[fid].tagline}
                    accent={factionVar(fid)}
                    rivalName={rivalId ? FACTIONS[rivalId].name : null}
                  />
                );
              })}
            </div>
          </section>

          {/* ── Emblem designer ──────────────────────────────────── */}
          <section className="space-y-2">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.1em] text-fg-2">
              Corporate Emblem
            </h2>
            <EmblemDesigner
              value={emblem}
              onChange={setEmblem}
              randomSeed={corpName || 'charter'}
            />
          </section>

          {error && <FormError message={error} />}

          <SubmitButton busy={busy} label="Register Charter" busyLabel="Registering…" />
        </form>

        <OAuthProviders />

        <div className="border-t border-line px-6 py-4 text-[11px] font-semibold">
          <Link to="/login" className="text-accent transition-colors hover:underline">
            ← Already registered? Login
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
}

// ── Subcomponents ──────────────────────────────────────────────────────────

/** Map a faction id to its CSS-variable color so swatches stay tokenized. */
function factionVar(fid: FactionId): string {
  return `var(--f-${fid})`;
}

function FactionCard({
  active,
  onClick,
  title,
  sub,
  accent,
  rivalName,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  sub: string;
  accent: string;
  rivalName: string | null;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'relative space-y-1 rounded-md border px-3 py-2.5 text-left transition-all',
        active
          ? 'border-accent bg-accent/5 ring-1 ring-accent/30'
          : 'border-line bg-panel-2 hover:border-line-strong hover:-translate-y-0.5',
      )}
    >
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className="h-2.5 w-2.5 flex-none rounded-sm"
          style={{ background: `rgb(${accent})` }}
        />
        <p className="text-[11px] font-bold text-fg">{title}</p>
      </div>
      <p className="font-mono text-[9.5px] text-muted">{sub}</p>
      {active && rivalName && (
        <p className="pt-0.5 font-mono text-[9.5px] text-bad">
          Rivals: {rivalName} (−2 rep)
        </p>
      )}
      {active && (
        <span
          aria-hidden
          className="absolute right-2 top-2 text-[9px] font-bold uppercase tracking-wide text-accent"
        >
          ✓
        </span>
      )}
    </button>
  );
}

// The `useMemo` import is left in for when we add derived preview values later.
void useMemo;
